import axios from "axios";
import {
  CalendarDays, ChevronDown, DollarSign, Eye, Loader2, MapPin, Percent, Repeat, Shuffle, Ticket, Users, Wallet,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import MotoHelmet from "@/components/icons/MotoHelmet";

import { ParaQuemVale } from "@/features/tipos-cliente";

import { DIAS, doCupom, erro, payload, resumo, sugestoes, textos } from "./regrasCupom";
import OndeVale from "./OndeVale";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const base = "h-10 rounded-lg border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-orange-500/30";
const campo = `${base} w-full`;

const TIPOS = [
  { k: "percentage", rotulo: "% de desconto", icone: Percent },
  { k: "fixed", rotulo: "R$ de desconto", icone: DollarSign },
  { k: "free_delivery", rotulo: "Frete grátis", icone: MotoHelmet },
];

function Regra({ id, aberta, onAbrir, icone: Icone, titulo, valor, children }) {
  const on = aberta === id;
  return (
    <div className={`rounded-xl border transition-colors ${on ? "border-orange-500/50 bg-orange-500/[0.03]" : "bg-card"}`}>
      <button type="button" onClick={() => onAbrir(on ? null : id)} className="w-full flex items-center gap-3 px-3.5 py-3 text-left" data-e2e={`cupom-regra-${id}`}>
        <span className="w-8 h-8 rounded-lg grid place-items-center bg-orange-500/10 text-orange-500 shrink-0"><Icone className="w-4 h-4" /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">{titulo}</span>
          <span className="block text-xs text-muted-foreground truncate">{valor}</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${on ? "rotate-180" : ""}`} />
      </button>
      {on && <div className="px-3.5 pb-3.5 pt-1 space-y-3">{children}</div>}
    </div>
  );
}

function Opcoes({ valor, opcoes, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {opcoes.map(([v, r]) => (
        <button key={String(v)} type="button" onClick={() => onChange(v)}
          className={`px-3 h-8 rounded-full border text-xs font-semibold ${valor === v ? "bg-orange-500 text-white border-orange-500" : "bg-background text-muted-foreground hover:border-orange-500/50"}`}>
          {r}
        </button>
      ))}
    </div>
  );
}

function Rotulo({ children, dica }) {
  return (
    <span className="block text-xs font-semibold mb-1">
      {children}{dica && <span className="font-normal text-muted-foreground"> · {dica}</span>}
    </span>
  );
}

function Previa({ f }) {
  const v = Number(String(f.discount_value || 0).replace(",", "."));
  return (
    <div className="relative rounded-2xl p-4 text-white bg-gradient-to-br from-orange-500 to-orange-600 shadow-lg overflow-hidden">
      <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-background" />
      <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-background" />
      <div className="flex items-center justify-between gap-3 px-2">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">Cupom</p>
          <p className="text-xl font-black tracking-wider truncate">{f.code || "CÓDIGO"}</p>
          <p className="text-[11px] opacity-85 truncate">{f.description || textos.minimo(f)}</p>
        </div>
        <div className="text-right shrink-0 border-l border-dashed border-white/40 pl-4">
          {f.discount_type === "free_delivery" ? <p className="text-lg font-black leading-tight">FRETE<br />GRÁTIS</p>
            : f.discount_type === "percentage" ? <p className="text-3xl font-black leading-none">{v || 0}<span className="text-lg">%</span></p>
              : <p className="text-2xl font-black leading-none"><span className="text-xs">R$</span>{(v || 0).toLocaleString("pt-BR")}</p>}
          <p className="text-[10px] font-bold opacity-80 mt-0.5">{f.discount_type === "free_delivery" ? "na entrega" : "OFF"}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Novo cupom / editar cupom (refeito em 23/10/2026, pedido do dono: "muito mais
 * eficiente e intuitivo"). O essencial em cima (tipo, valor, código); as regras
 * viram linhas que mostram o valor atual e só abrem quando o lojista toca —
 * com o padrão que serve para a maioria dos cupons. A frase do rodapé resume o
 * cupom inteiro antes de salvar.
 */
// `tipoInicial`: cupom novo vindo do botão de ofertas da tela de Clientes já nasce para aquele tipo
export default function CupomForm({ open, cupom, onClose, onSalvo, tipoInicial = null }) {
  const [f, setF] = useState(doCupom(cupom));
  const [aberta, setAberta] = useState(null);
  const [salvando, setSalvando] = useState(false);
  useEffect(() => {
    if (!open) return;
    const novo = doCupom(cupom);
    if (!cupom && tipoInicial) novo.segmentos = [tipoInicial];
    setF(novo);
    setAberta(!cupom && tipoInicial ? "quem" : null);
  }, [open, cupom, tipoInicial]);
  const m = (campos) => setF((x) => ({ ...x, ...campos }));
  const alternarDia = (d) => m({ valid_weekdays: f.valid_weekdays.includes(d) ? f.valid_weekdays.filter((x) => x !== d) : [...f.valid_weekdays, d] });
  const gerar = () => m({ code: Array.from({ length: 8 }, () => "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"[Math.floor(Math.random() * 32)]).join("") });

  const salvar = async () => {
    const e = erro(f, !cupom);
    if (e) { toast.error(e); return; }
    setSalvando(true);
    try {
      if (cupom) await axios.put(`/api/coupons/${cupom.id}`, payload(f), auth());
      else await axios.post("/api/coupons", payload(f), auth());
      toast.success(cupom ? "Cupom atualizado" : "Cupom criado");
      onSalvo?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível salvar o cupom");
    } finally {
      setSalvando(false);
    }
  };

  const periodo = f.valid_from || f.valid_until ? "datas" : "sempre";
  const horario = f.time_from || f.time_until ? "janela" : "todo";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[100vw] sm:max-w-xl w-full h-[100dvh] sm:h-auto sm:max-h-[94dvh] p-0 gap-0 flex flex-col rounded-none sm:rounded-2xl overflow-hidden" data-e2e="cupom-form">
        <DialogHeader className="px-5 py-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg"><Ticket className="w-5 h-5 text-orange-500" /> {cupom ? "Editar cupom" : "Novo cupom"}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <Previa f={f} />

          <section className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {TIPOS.map(({ k, rotulo, icone: Icone }) => (
                <button key={k} type="button" onClick={() => m({ discount_type: k })} data-e2e={`cupom-tipo-${k}`}
                  className={`flex flex-col items-center gap-1 rounded-xl border-2 p-3 text-xs font-semibold transition-colors ${f.discount_type === k ? "border-orange-500 bg-orange-500/5 text-orange-600 dark:text-orange-400" : "border-transparent bg-muted/50 text-muted-foreground hover:bg-muted"}`}>
                  <Icone className="w-5 h-5" /> {rotulo}
                </button>
              ))}
            </div>
            {f.discount_type !== "free_delivery" && (
              <div className="grid grid-cols-2 gap-3">
                <label><Rotulo>{f.discount_type === "percentage" ? "Desconto (%)" : "Desconto (R$)"}</Rotulo>
                  <input className={`${campo} text-lg font-bold`} inputMode="decimal" value={f.discount_value} placeholder={f.discount_type === "percentage" ? "Ex.: 10" : "Ex.: 15,00"}
                    onChange={(e) => m({ discount_value: e.target.value.replace(/[^\d.,]/g, "") })} data-e2e="cupom-valor" /></label>
                {f.discount_type === "percentage" && (
                  <label><Rotulo dica="opcional">Desconto máximo (R$)</Rotulo>
                    <input className={campo} inputMode="decimal" value={f.max_discount_value} placeholder="Sem limite"
                      onChange={(e) => m({ max_discount_value: e.target.value.replace(/[^\d.,]/g, "") })} /></label>
                )}
              </div>
            )}
            <div>
              <Rotulo dica="o que o cliente digita">Código</Rotulo>
              <div className="flex gap-2">
                <input className={`${campo} font-mono font-bold tracking-wider uppercase`} value={f.code} maxLength={30} placeholder="EX.: BEMVINDO10"
                  onChange={(e) => m({ code: e.target.value.toUpperCase().replace(/\s/g, "") })} data-e2e="cupom-codigo" />
                <button type="button" onClick={gerar} title="Gerar código" className="h-10 w-10 shrink-0 grid place-items-center rounded-lg border hover:bg-muted"><Shuffle className="w-4 h-4" /></button>
              </div>
              {!f.code && sugestoes(f).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {sugestoes(f).map((s) => <button key={s} type="button" onClick={() => m({ code: s })} className="px-2 h-7 rounded-md bg-muted text-[11px] font-mono font-bold hover:bg-orange-500/10">{s}</button>)}
                </div>
              )}
            </div>
            <label className="block"><Rotulo dica="opcional">Texto para o cliente</Rotulo>
              <input className={campo} value={f.description} maxLength={120} placeholder="Ex.: 10% na sua primeira compra"
                onChange={(e) => m({ description: e.target.value })} /></label>
          </section>

          <section className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Regras · toque para mudar</p>
            <Regra id="minimo" aberta={aberta} onAbrir={setAberta} icone={Wallet} titulo="Pedido mínimo" valor={textos.minimo(f)}>
              <label className="block"><Rotulo dica="sem o frete; vazio = qualquer valor">Valor mínimo em produtos (R$)</Rotulo>
                <input className={campo} inputMode="decimal" value={f.min_order_value} placeholder="Sem mínimo"
                  onChange={(e) => m({ min_order_value: e.target.value.replace(/[^\d.,]/g, "") })} /></label>
            </Regra>
            <Regra id="quem" aberta={aberta} onAbrir={setAberta} icone={Users} titulo="Quem pode usar" valor={textos.quem(f)}>
              <Opcoes valor={f.first_order_only ? "primeiro" : "todos"} onChange={(v) => m({ first_order_only: v === "primeiro" })}
                opcoes={[["todos", "Qualquer cliente"], ["primeiro", "Só no primeiro pedido"]]} />
              <div className="pt-1 border-t">
                <ParaQuemVale valor={f.segmentos} aoMudar={(segmentos) => m({ segmentos })} e2e="cupom-para-quem"
                  ajuda="Os tipos da tela de Clientes. Marcado, o cupom só funciona para o cliente identificado desses tipos e só aparece no cardápio para ele." />
              </div>
            </Regra>
            <Regra id="vezes" aberta={aberta} onAbrir={setAberta} icone={Repeat} titulo="Quantas vezes" valor={textos.vezes(f)}>
              <div><Rotulo>Cada cliente pode usar</Rotulo>
                <Opcoes valor={["1", "2", "3", ""].includes(f.usage_limit_per_customer) ? f.usage_limit_per_customer : "outro"}
                  onChange={(v) => m({ usage_limit_per_customer: v === "outro" ? "5" : v })}
                  opcoes={[["1", "1 vez"], ["2", "2 vezes"], ["3", "3 vezes"], ["", "Sem limite"], ["outro", "Outro"]]} />
                {!["1", "2", "3", ""].includes(f.usage_limit_per_customer) && (
                  <input className={`${base} mt-2 w-28`} inputMode="numeric" value={f.usage_limit_per_customer}
                    onChange={(e) => m({ usage_limit_per_customer: e.target.value.replace(/\D/g, "") })} />
                )}
              </div>
              <label className="block"><Rotulo dica="vazio = sem limite">Usos no total (todos os clientes)</Rotulo>
                <input className={`${base} w-40`} inputMode="numeric" value={f.usage_limit} placeholder="Sem limite"
                  onChange={(e) => m({ usage_limit: e.target.value.replace(/\D/g, "") })} /></label>
            </Regra>
            <Regra id="quando" aberta={aberta} onAbrir={setAberta} icone={CalendarDays} titulo="Quando vale" valor={textos.quando(f)}>
              <div><Rotulo>Período</Rotulo>
                <Opcoes valor={periodo} onChange={(v) => (v === "sempre" ? m({ valid_from: "", valid_until: "" }) : m({ valid_from: f.valid_from || new Date().toLocaleDateString("sv-SE") }))}
                  opcoes={[["sempre", "Sem data para acabar"], ["datas", "Entre datas"]]} />
                {periodo === "datas" && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <label><Rotulo>Começa em</Rotulo><input type="date" className={campo} value={f.valid_from} onChange={(e) => m({ valid_from: e.target.value })} /></label>
                    <label><Rotulo>Termina em</Rotulo><input type="date" className={campo} value={f.valid_until} min={f.valid_from || undefined} onChange={(e) => m({ valid_until: e.target.value })} /></label>
                  </div>
                )}
              </div>
              <div><Rotulo dica="nenhum marcado = todos os dias">Dias da semana</Rotulo>
                <div className="flex flex-wrap gap-1.5">
                  {DIAS.map((d) => (
                    <button key={d.v} type="button" onClick={() => alternarDia(d.v)}
                      className={`w-11 h-9 rounded-lg text-xs font-bold ${f.valid_weekdays.includes(d.v) ? "bg-orange-500 text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>{d.curto}</button>
                  ))}
                </div>
              </div>
              <div><Rotulo>Horário</Rotulo>
                <Opcoes valor={horario} onChange={(v) => (v === "todo" ? m({ time_from: "", time_until: "" }) : m({ time_from: f.time_from || "18:00", time_until: f.time_until || "23:00" }))}
                  opcoes={[["todo", "O dia todo"], ["janela", "Só num horário"]]} />
                {horario === "janela" && (
                  <div className="flex items-center gap-2 mt-2 text-sm">
                    das <input type="time" className={`${base} w-32`} value={f.time_from} onChange={(e) => m({ time_from: e.target.value })} />
                    às <input type="time" className={`${base} w-32`} value={f.time_until} onChange={(e) => m({ time_until: e.target.value })} />
                  </div>
                )}
                {horario === "janela" && f.time_from && f.time_until && f.time_from > f.time_until && (
                  <p className="text-xs text-muted-foreground mt-1">Passa da meia-noite: vale até as {f.time_until} do dia seguinte.</p>
                )}
              </div>
            </Regra>
            <Regra id="onde" aberta={aberta} onAbrir={setAberta} icone={MapPin} titulo="Onde vale" valor={textos.onde(f)}>
              <OndeVale modulos={f.modules} onMudar={(modules) => m({ modules })} />
            </Regra>
            <Regra id="cardapio" aberta={aberta} onAbrir={setAberta} icone={Eye} titulo="No cardápio" valor={textos.cardapio(f)}>
              <label className="flex items-center justify-between gap-3 text-sm">
                <span>Mostrar na lista de cupons do cardápio<span className="block text-xs text-muted-foreground">Desligado, o cupom só vale para quem digitar o código.</span></span>
                <Switch checked={f.visible_to_customer} onCheckedChange={(v) => m({ visible_to_customer: v })} />
              </label>
              {f.discount_type !== "free_delivery" && (
                <label className="flex items-center justify-between gap-3 text-sm">
                  <span>O desconto também vale sobre o frete<span className="block text-xs text-muted-foreground">Desligado, o desconto é só nos produtos.</span></span>
                  <Switch checked={f.applies_to_delivery} onCheckedChange={(v) => m({ applies_to_delivery: v })} />
                </label>
              )}
            </Regra>
          </section>
        </div>

        <div className="border-t bg-muted/30 px-5 py-3 space-y-3 shrink-0">
          <p className="text-xs text-muted-foreground leading-snug" data-e2e="cupom-resumo">{resumo(f)}</p>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm font-medium mr-auto">
              <Switch checked={f.is_active} onCheckedChange={(v) => m({ is_active: v })} /> {f.is_active ? "Ativo" : "Pausado"}
            </label>
            <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border text-sm font-semibold hover:bg-muted">Cancelar</button>
            <button type="button" onClick={salvar} disabled={salvando} data-e2e="cupom-salvar"
              className="h-10 px-5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold disabled:opacity-60 inline-flex items-center gap-2">
              {salvando && <Loader2 className="w-4 h-4 animate-spin" />} {cupom ? "Salvar" : "Criar cupom"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
